import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Users, FileText, Send, CheckCircle, History, AlertTriangle,
  Plus, Trash2, Upload, Calendar, Clock, User, Building2,
  FileCheck, ExternalLink, Edit3, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

// Tipos de evidência
const EVIDENCE_TYPES = [
  { value: 'FOTO', label: 'Foto' },
  { value: 'PORTARIA', label: 'Portaria' },
  { value: 'RELATORIO', label: 'Relatório' },
  { value: 'DIARIO_OFICIAL', label: 'Diário Oficial' },
  { value: 'DOCUMENTO', label: 'Documento' },
  { value: 'OUTRO', label: 'Outro' }
];

// Status do protocolo externo
const PROTOCOL_STATUSES = [
  { value: 'ENVIADO', label: 'Enviado', color: 'bg-blue-100 text-blue-800' },
  { value: 'RECEBIDO', label: 'Recebido', color: 'bg-indigo-100 text-blue-800' },
  { value: 'EM_ANALISE', label: 'Em Análise', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'APROVADO', label: 'Aprovado', color: 'bg-green-100 text-green-800' },
  { value: 'REJEITADO', label: 'Rejeitado', color: 'bg-red-100 text-red-800' },
  { value: 'PARCIAL', label: 'Parcial', color: 'bg-orange-100 text-orange-800' }
];

// Resultado final
const FINAL_RESULTS = [
  { value: 'CUMPRIDO', label: 'Cumprido', color: 'bg-green-100 text-green-800' },
  { value: 'CUMPRIDO_PARCIALMENTE', label: 'Cumprido Parcialmente', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'NAO_CUMPRIDO', label: 'Não Cumprido', color: 'bg-red-100 text-red-800' },
  { value: 'JUDICIALIZADO', label: 'Judicializado', color: 'bg-blue-100 text-blue-800' },
  { value: 'SEM_COMPETENCIA', label: 'Sem Competência', color: 'bg-gray-100 text-gray-800' }
];

// Eventos do histórico
const HISTORY_EVENTS = {
  'CRIACAO': { label: 'Criação do Ato', icon: FileText, color: 'text-blue-600' },
  'ATRIBUICAO_UNIDADE': { label: 'Atribuição de Unidade', icon: Building2, color: 'text-blue-600' },
  'ATRIBUICAO_RESPONSAVEL': { label: 'Atribuição de Responsável', icon: User, color: 'text-indigo-600' },
  'EVIDENCIA_ADICIONADA': { label: 'Evidência Adicionada', icon: FileCheck, color: 'text-green-600' },
  'EVIDENCIA_REMOVIDA': { label: 'Evidência Removida', icon: Trash2, color: 'text-red-600' },
  'RESPOSTA_OFICIAL_CRIADA': { label: 'Resposta Oficial Criada', icon: Send, color: 'text-blue-600' },
  'RESPOSTA_OFICIAL_ATUALIZADA': { label: 'Resposta Oficial Atualizada', icon: Edit3, color: 'text-blue-600' },
  'PROTOCOLO_EXTERNO_REGISTRADO': { label: 'Protocolo Externo Registrado', icon: ExternalLink, color: 'text-blue-600' },
  'PROTOCOLO_EXTERNO_ATUALIZADO': { label: 'Protocolo Externo Atualizado', icon: ExternalLink, color: 'text-blue-600' },
  'STATUS_PROTOCOLO_ALTERADO': { label: 'Status do Protocolo Alterado', icon: AlertTriangle, color: 'text-yellow-600' },
  'RESULTADO_FINAL_DEFINIDO': { label: 'Resultado Final Definido', icon: CheckCircle, color: 'text-green-600' },
  'RETIFICACAO_REGISTRADA': { label: 'Retificação Registrada', icon: Edit3, color: 'text-orange-600' }
};

const ComplianceCycleComponent = ({ actId, act, onUpdate }) => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('responsibility');
  const [loading, setLoading] = useState(false);
  
  // States for data
  const [evidences, setEvidences] = useState([]);
  const [officialResponse, setOfficialResponse] = useState(null);
  const [externalProtocol, setExternalProtocol] = useState(null);
  const [history, setHistory] = useState([]);
  const [retifications, setRetifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  
  // Dialog states
  const [showEvidenceDialog, setShowEvidenceDialog] = useState(false);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [showProtocolDialog, setShowProtocolDialog] = useState(false);
  const [showRetificationDialog, setShowRetificationDialog] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  
  // Form states
  const [newEvidence, setNewEvidence] = useState({ evidence_type: 'DOCUMENTO', description: '' });
  const [newResponse, setNewResponse] = useState({ parecer_minuta: '', observacoes_internas: '', observacoes_orgao_externo: '' });
  const [newProtocol, setNewProtocol] = useState({ numero_protocolo: '', data_envio: '', status: 'ENVIADO' });
  const [newRetification, setNewRetification] = useState({ descricao: '', justificativa: '' });
  const [selectedResult, setSelectedResult] = useState('');
  const [responsibility, setResponsibility] = useState({ 
    unidade_responsavel_interna: act?.unidade_responsavel_interna || '', 
    responsavel_pelo_ato: act?.responsavel_pelo_ato || '' 
  });

  const fetchData = useCallback(async () => {
    try {
      const [evidencesRes, responseRes, protocolRes, historyRes, retificationsRes, usersRes] = await Promise.all([
        api.get(`/acts/${actId}/compliance-evidences`),
        api.get(`/acts/${actId}/official-response`),
        api.get(`/acts/${actId}/external-protocol`),
        api.get(`/acts/${actId}/history`),
        api.get(`/acts/${actId}/retifications`),
        api.get('/users').catch(() => ({ data: [] }))
      ]);
      
      setEvidences(evidencesRes.data.evidences || []);
      setOfficialResponse(responseRes.data || null);
      setExternalProtocol(protocolRes.data || null);
      setHistory(historyRes.data.history || []);
      setRetifications(retificationsRes.data.retifications || []);
      setUsers(usersRes.data || []);
      
      // Mock units based on act
      setUnits([
        { id: 'JURIDICO', name: 'Assessoria Jurídica' },
        { id: 'CONTROLADORIA', name: 'Controladoria' },
        { id: 'FINANCEIRO', name: 'Financeiro' },
        { id: 'ADMINISTRATIVO', name: 'Administrativo' },
        { id: 'TI', name: 'Tecnologia da Informação' }
      ]);
    } catch (error) {
      console.error('Error fetching compliance data:', error);
    }
  }, [api, actId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateResponsibility = async () => {
    setLoading(true);
    try {
      await api.put(`/acts/${actId}/responsibility`, responsibility);
      toast.success('Responsabilidade atualizada');
      fetchData();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Erro ao atualizar responsabilidade');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvidence = async () => {
    setLoading(true);
    try {
      await api.post(`/acts/${actId}/compliance-evidences`, newEvidence);
      toast.success('Evidência adicionada');
      setShowEvidenceDialog(false);
      setNewEvidence({ evidence_type: 'DOCUMENTO', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao adicionar evidência');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvidence = async (evidenceId) => {
    if (!window.confirm('Deseja remover esta evidência?')) return;
    try {
      await api.delete(`/acts/${actId}/compliance-evidences/${evidenceId}`);
      toast.success('Evidência removida');
      fetchData();
    } catch (error) {
      toast.error('Erro ao remover evidência');
    }
  };

  const handleSaveResponse = async () => {
    setLoading(true);
    try {
      await api.post(`/acts/${actId}/official-response`, newResponse);
      toast.success('Resposta oficial salva');
      setShowResponseDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar resposta');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProtocol = async () => {
    setLoading(true);
    try {
      await api.post(`/acts/${actId}/external-protocol`, {
        ...newProtocol,
        data_envio: new Date(newProtocol.data_envio).toISOString()
      });
      toast.success('Protocolo registrado');
      setShowProtocolDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao registrar protocolo');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRetification = async () => {
    setLoading(true);
    try {
      await api.post(`/acts/${actId}/retifications`, newRetification);
      toast.success('Retificação registrada');
      setShowRetificationDialog(false);
      setNewRetification({ descricao: '', justificativa: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao registrar retificação');
    } finally {
      setLoading(false);
    }
  };

  const handleSetFinalResult = async () => {
    setLoading(true);
    try {
      await api.put(`/acts/${actId}/final-result?resultado=${selectedResult}`);
      toast.success('Resultado final definido');
      setShowResultDialog(false);
      fetchData();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao definir resultado');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4" data-testid="compliance-cycle">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-7 w-full">
          <TabsTrigger value="responsibility" data-testid="tab-responsibility">
            <Users className="w-4 h-4 mr-1" /> Responsabilidade
          </TabsTrigger>
          <TabsTrigger value="evidences" data-testid="tab-evidences">
            <FileCheck className="w-4 h-4 mr-1" /> Evidências
          </TabsTrigger>
          <TabsTrigger value="response" data-testid="tab-response">
            <Send className="w-4 h-4 mr-1" /> Resposta
          </TabsTrigger>
          <TabsTrigger value="protocol" data-testid="tab-protocol">
            <ExternalLink className="w-4 h-4 mr-1" /> Protocolo
          </TabsTrigger>
          <TabsTrigger value="result" data-testid="tab-result">
            <CheckCircle className="w-4 h-4 mr-1" /> Resultado
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="w-4 h-4 mr-1" /> Histórico
          </TabsTrigger>
          <TabsTrigger value="retifications" data-testid="tab-retifications">
            <Edit3 className="w-4 h-4 mr-1" /> Retificações
          </TabsTrigger>
        </TabsList>

        {/* Responsabilidade */}
        <TabsContent value="responsibility">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-5 h-5" /> Responsabilidade do Ato
              </CardTitle>
              <CardDescription>Defina a unidade e o responsável interno pelo cumprimento</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Unidade Responsável Interna *</label>
                  <Select 
                    value={responsibility.unidade_responsavel_interna} 
                    onValueChange={(v) => setResponsibility({...responsibility, unidade_responsavel_interna: v})}
                  >
                    <SelectTrigger data-testid="select-unit">
                      <SelectValue placeholder="Selecione a unidade" />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Responsável pelo Ato *</label>
                  <Select 
                    value={responsibility.responsavel_pelo_ato} 
                    onValueChange={(v) => setResponsibility({...responsibility, responsavel_pelo_ato: v})}
                  >
                    <SelectTrigger data-testid="select-responsible">
                      <SelectValue placeholder="Selecione o responsável" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={handleUpdateResponsibility} disabled={loading} data-testid="btn-save-responsibility">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Salvar Responsabilidade
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Evidências */}
        <TabsContent value="evidences">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Evidências de Cumprimento</CardTitle>
                <CardDescription>Documentos comprobatórios do cumprimento do ato</CardDescription>
              </div>
              <Button onClick={() => setShowEvidenceDialog(true)} size="sm" data-testid="btn-add-evidence">
                <Plus className="w-4 h-4 mr-1" /> Nova Evidência
              </Button>
            </CardHeader>
            <CardContent>
              {evidences.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Nenhuma evidência cadastrada</p>
              ) : (
                <div className="space-y-2">
                  {evidences.map(ev => (
                    <div key={ev.evidence_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileCheck className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-medium text-sm">{ev.description}</p>
                          <p className="text-xs text-slate-500">
                            {EVIDENCE_TYPES.find(t => t.value === ev.evidence_type)?.label} • {formatDate(ev.created_at)}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteEvidence(ev.evidence_id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Resposta Oficial */}
        <TabsContent value="response">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Resposta Oficial ao Órgão Externo</CardTitle>
                <CardDescription>Parecer/minuta e observações para envio</CardDescription>
              </div>
              <Button onClick={() => {
                setNewResponse({
                  parecer_minuta: officialResponse?.parecer_minuta || '',
                  observacoes_internas: officialResponse?.observacoes_internas || '',
                  observacoes_orgao_externo: officialResponse?.observacoes_orgao_externo || ''
                });
                setShowResponseDialog(true);
              }} size="sm" data-testid="btn-edit-response">
                <Edit3 className="w-4 h-4 mr-1" /> {officialResponse ? 'Editar' : 'Criar'} Resposta
              </Button>
            </CardHeader>
            <CardContent>
              {officialResponse ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-slate-600">Parecer / Minuta</label>
                    <p className="mt-1 p-3 bg-slate-50 rounded text-sm whitespace-pre-wrap">{officialResponse.parecer_minuta}</p>
                  </div>
                  {officialResponse.observacoes_orgao_externo && (
                    <div>
                      <label className="text-sm font-medium text-slate-600">Observações ao Órgão</label>
                      <p className="mt-1 p-3 bg-slate-50 rounded text-sm">{officialResponse.observacoes_orgao_externo}</p>
                    </div>
                  )}
                  {officialResponse.observacoes_internas && (
                    <div>
                      <label className="text-sm font-medium text-slate-600">Observações Internas (não enviadas)</label>
                      <p className="mt-1 p-3 bg-yellow-50 rounded text-sm italic">{officialResponse.observacoes_internas}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">Nenhuma resposta oficial registrada</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Protocolo Externo */}
        <TabsContent value="protocol">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Protocolo Externo</CardTitle>
                <CardDescription>Registro do envio ao órgão de controle</CardDescription>
              </div>
              <Button onClick={() => {
                setNewProtocol({
                  numero_protocolo: externalProtocol?.numero_protocolo || '',
                  data_envio: externalProtocol?.data_envio?.split('T')[0] || '',
                  status: externalProtocol?.status || 'ENVIADO'
                });
                setShowProtocolDialog(true);
              }} size="sm" data-testid="btn-edit-protocol">
                <ExternalLink className="w-4 h-4 mr-1" /> {externalProtocol ? 'Editar' : 'Registrar'} Protocolo
              </Button>
            </CardHeader>
            <CardContent>
              {externalProtocol && externalProtocol.numero_protocolo ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600">Nº Protocolo</label>
                    <p className="font-mono text-lg">{externalProtocol.numero_protocolo}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Data de Envio</label>
                    <p>{formatDate(externalProtocol.data_envio)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Data de Recebimento</label>
                    <p>{externalProtocol.data_recebimento ? formatDate(externalProtocol.data_recebimento) : '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Status</label>
                    <Badge className={PROTOCOL_STATUSES.find(s => s.value === externalProtocol.status)?.color}>
                      {PROTOCOL_STATUSES.find(s => s.value === externalProtocol.status)?.label}
                    </Badge>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">Nenhum protocolo externo registrado</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Resultado Final */}
        <TabsContent value="result">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Resultado Final do Ato</CardTitle>
                <CardDescription>Defina o resultado após protocolo ou encerramento</CardDescription>
              </div>
              <Button 
                onClick={() => setShowResultDialog(true)} 
                size="sm"
                disabled={!externalProtocol?.numero_protocolo && !['ENCERRADO', 'ARQUIVADO'].includes(act?.status)}
                data-testid="btn-set-result"
              >
                <CheckCircle className="w-4 h-4 mr-1" /> Definir Resultado
              </Button>
            </CardHeader>
            <CardContent>
              {act?.resultado_final ? (
                <div className="flex items-center gap-4">
                  <Badge className={`text-lg px-4 py-2 ${FINAL_RESULTS.find(r => r.value === act.resultado_final)?.color}`}>
                    {FINAL_RESULTS.find(r => r.value === act.resultado_final)?.label}
                  </Badge>
                  <span className="text-sm text-slate-500">
                    Definido em {formatDate(act.resultado_final_data)}
                  </span>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-slate-500">Resultado final ainda não definido</p>
                  {!externalProtocol?.numero_protocolo && !['ENCERRADO', 'ARQUIVADO'].includes(act?.status) && (
                    <p className="text-xs text-slate-400 mt-1">
                      Necessário protocolo externo ou encerramento interno para definir resultado
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Histórico */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="w-5 h-5" /> Histórico do Ato (Timeline)
              </CardTitle>
              <CardDescription>Registro cronológico de todas as movimentações</CardDescription>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Nenhum evento registrado</p>
              ) : (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
                  <div className="space-y-4">
                    {history.map((evt, idx) => {
                      const config = HISTORY_EVENTS[evt.evento] || { label: evt.evento, icon: Clock, color: 'text-slate-600' };
                      const Icon = config.icon;
                      return (
                        <div key={evt.event_id || idx} className="relative pl-10">
                          <div className={`absolute left-2 w-5 h-5 rounded-full bg-white border-2 flex items-center justify-center ${config.color.replace('text-', 'border-')}`}>
                            <Icon className={`w-3 h-3 ${config.color}`} />
                          </div>
                          <div className="bg-slate-50 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-sm">{config.label}</span>
                              <span className="text-xs text-slate-500">{formatDate(evt.data_hora)}</span>
                            </div>
                            <p className="text-xs text-slate-600 mt-1">{evt.user_email}</p>
                            {evt.metadados && Object.keys(evt.metadados).length > 0 && (
                              <p className="text-xs text-slate-500 mt-1">
                                {Object.entries(evt.metadados).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Retificações */}
        <TabsContent value="retifications">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Retificações</CardTitle>
                <CardDescription>Correções e ajustes realizados no ato</CardDescription>
              </div>
              <Button onClick={() => setShowRetificationDialog(true)} size="sm" data-testid="btn-add-retification">
                <Plus className="w-4 h-4 mr-1" /> Nova Retificação
              </Button>
            </CardHeader>
            <CardContent>
              {retifications.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Nenhuma retificação registrada</p>
              ) : (
                <div className="space-y-3">
                  {retifications.map(ret => (
                    <div key={ret.retification_id} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{ret.descricao}</span>
                        <span className="text-xs text-slate-500">{formatDate(ret.created_at)}</span>
                      </div>
                      <p className="text-sm text-slate-600"><strong>Justificativa:</strong> {ret.justificativa}</p>
                      <p className="text-xs text-slate-500 mt-1">Por: {ret.created_by_email}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog: Nova Evidência */}
      <Dialog open={showEvidenceDialog} onOpenChange={setShowEvidenceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Evidência de Cumprimento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Tipo de Evidência *</label>
              <Select value={newEvidence.evidence_type} onValueChange={(v) => setNewEvidence({...newEvidence, evidence_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVIDENCE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Descrição *</label>
              <Textarea 
                value={newEvidence.description} 
                onChange={(e) => setNewEvidence({...newEvidence, description: e.target.value})}
                placeholder="Descreva a evidência..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEvidenceDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateEvidence} disabled={loading || !newEvidence.description}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Resposta Oficial */}
      <Dialog open={showResponseDialog} onOpenChange={setShowResponseDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Resposta Oficial ao Órgão Externo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Parecer / Minuta *</label>
              <Textarea 
                value={newResponse.parecer_minuta} 
                onChange={(e) => setNewResponse({...newResponse, parecer_minuta: e.target.value})}
                placeholder="Digite o parecer ou minuta de resposta..."
                rows={6}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Observações ao Órgão Externo</label>
              <Textarea 
                value={newResponse.observacoes_orgao_externo} 
                onChange={(e) => setNewResponse({...newResponse, observacoes_orgao_externo: e.target.value})}
                placeholder="Observações que serão enviadas ao órgão..."
                rows={2}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Observações Internas (não enviadas)</label>
              <Textarea 
                value={newResponse.observacoes_internas} 
                onChange={(e) => setNewResponse({...newResponse, observacoes_internas: e.target.value})}
                placeholder="Observações internas da equipe..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResponseDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveResponse} disabled={loading || !newResponse.parecer_minuta}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salvar Resposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Protocolo Externo */}
      <Dialog open={showProtocolDialog} onOpenChange={setShowProtocolDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Protocolo Externo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Nº do Protocolo *</label>
              <Input 
                value={newProtocol.numero_protocolo} 
                onChange={(e) => setNewProtocol({...newProtocol, numero_protocolo: e.target.value})}
                placeholder="Ex: 2026/001234"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Data de Envio *</label>
              <Input 
                type="date"
                value={newProtocol.data_envio} 
                onChange={(e) => setNewProtocol({...newProtocol, data_envio: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Status</label>
              <Select value={newProtocol.status} onValueChange={(v) => setNewProtocol({...newProtocol, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROTOCOL_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProtocolDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveProtocol} disabled={loading || !newProtocol.numero_protocolo || !newProtocol.data_envio}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salvar Protocolo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Retificação */}
      <Dialog open={showRetificationDialog} onOpenChange={setShowRetificationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Retificação</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Descrição da Retificação *</label>
              <Input 
                value={newRetification.descricao} 
                onChange={(e) => setNewRetification({...newRetification, descricao: e.target.value})}
                placeholder="O que foi retificado?"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Justificativa *</label>
              <Textarea 
                value={newRetification.justificativa} 
                onChange={(e) => setNewRetification({...newRetification, justificativa: e.target.value})}
                placeholder="Por que a retificação foi necessária?"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRetificationDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateRetification} disabled={loading || !newRetification.descricao || !newRetification.justificativa}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Resultado Final */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Definir Resultado Final</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Resultado Final *</label>
              <Select value={selectedResult} onValueChange={setSelectedResult}>
                <SelectTrigger><SelectValue placeholder="Selecione o resultado" /></SelectTrigger>
                <SelectContent>
                  {FINAL_RESULTS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResultDialog(false)}>Cancelar</Button>
            <Button onClick={handleSetFinalResult} disabled={loading || !selectedResult}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComplianceCycleComponent;
